import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ActivityItem {
  id: string;
  userName: string;
  agentName: string;
  score: number;
  date: string;
  status: 'completed' | 'in_progress';
  userInitials?: string;
}

interface RecentActivityProps {
  title: string;
  activities: ActivityItem[];
  showUserInfo?: boolean;
}

export function RecentActivity({ title, activities, showUserInfo = false }: RecentActivityProps) {
  const navigate = useNavigate();

  const getStatusBadge = (status: string) => {
    if (status === 'completed') {
      return <Badge className="bg-success/10 text-success">Completed</Badge>;
    }
    return <Badge className="bg-info/10 text-info">In Progress</Badge>;
  };

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'bg-success';
    if (score >= 70) return 'bg-warning';
    return 'bg-destructive';
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    
    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffHours < 48) return 'Yesterday';
    return date.toLocaleDateString('en-AU', { month: 'short', day: 'numeric' });
  };

  return (
    <section className="bg-card rounded-lg p-4 sm:p-6 shadow-sm border border-border">
      <h2 className="text-xl font-bold text-foreground mb-4">{title}</h2>

      <div className="divide-y divide-border md:hidden">
        {activities.map((activity) => (
          <article key={activity.id} className="py-4 first:pt-0 last:pb-0">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {showUserInfo && (
                  <p className="mb-1 text-xs font-medium text-muted-foreground">
                    {activity.userName}
                  </p>
                )}
                <h3 className="truncate text-sm font-semibold text-foreground">
                  {activity.agentName}
                </h3>
                <p className="mt-1 text-xs text-muted-foreground">
                  {formatDate(activity.date)}
                </p>
              </div>
              {getStatusBadge(activity.status)}
            </div>

            <div className="mt-3 flex items-center gap-3">
              <div className="h-2 flex-1 rounded-full bg-muted" aria-hidden="true">
                <div
                  className={`h-2 rounded-full transition-all ${getScoreColor(activity.score)}`}
                  style={{ width: `${activity.score}%` }}
                />
              </div>
              <span
                className="min-w-[3.75rem] text-right text-sm font-semibold text-foreground"
                aria-label={`Score ${activity.score} out of 100`}
              >
                {activity.score}/100
              </span>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="mt-3 w-full"
              onClick={() => navigate(`/simulation/results/${activity.id}`)}
            >
              <Eye className="mr-2 h-4 w-4" />
              View Details
            </Button>
          </article>
        ))}
      </div>

      <div className="hidden overflow-x-auto md:block">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              {showUserInfo && (
                <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                  User
                </th>
              )}
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                Agent
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                Score
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                Date
              </th>
              <th className="text-left py-3 px-4 text-sm font-semibold text-muted-foreground">
                Status
              </th>
              <th className="text-right py-3 px-4 text-sm font-semibold text-muted-foreground">
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {activities.map((activity) => (
              <tr
                key={activity.id}
                className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors"
              >
                {showUserInfo && (
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-xs font-semibold">
                        {activity.userInitials || activity.userName.slice(0, 2).toUpperCase()}
                      </div>
                      <span className="text-sm font-medium text-foreground">
                        {activity.userName}
                      </span>
                    </div>
                  </td>
                )}
                <td className="py-3 px-4">
                  <span className="text-sm text-foreground">{activity.agentName}</span>
                </td>
                <td className="py-3 px-4">
                  <div className="flex items-center gap-2">
                    <div className="flex-1 max-w-[120px] bg-muted rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all ${getScoreColor(
                          activity.score
                        )}`}
                        style={{ width: `${activity.score}%` }}
                      />
                    </div>
                    <span className="text-sm font-semibold text-foreground min-w-[50px]">
                      {activity.score}/100
                    </span>
                  </div>
                </td>
                <td className="py-3 px-4">
                  <span className="text-sm text-muted-foreground">
                    {formatDate(activity.date)}
                  </span>
                </td>
                <td className="py-3 px-4">{getStatusBadge(activity.status)}</td>
                <td className="py-3 px-4 text-right">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-primary hover:text-primary-light"
                    onClick={() => {
                      navigate(`/simulation/results/${activity.id}`);
                    }}
                  >
                    <Eye className="w-4 h-4 mr-1" />
                    View Details
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
